import { useState, useEffect, useMemo } from 'react';
import {
  Package,
  Search,
  Filter,
  Eye,
  EyeOff,
  Star,
  DollarSign,
  Percent,
  SlidersHorizontal,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  TrendingUp,
  RotateCcw,
  Check,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert.jsx';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table.jsx';
import { formatCurrency } from '../../lib/utils.js';
import { getProducts, getCategories, apiFetch, getAdminApiKey } from '../../lib/api.js';
import { updateAdminProductDisplay, PRODUCT_EDIT_TEXTS } from '../../lib/product-display.js';

export function CatalogAssortmentTab({
  tenants = [],
  activeTenant,
  onSelectTenant,
  apiKey,
  initialError = null,
  initialProducts = null,
  initialCategories = null,
}) {
  const [selectedTenantId, setSelectedTenantId] = useState(activeTenant?.id || (tenants[0]?.id ?? ''));

  // Synchronize local selection with activeTenant prop changes (e.g. AdminHeader tenant switch)
  useEffect(() => {
    if (activeTenant?.id && activeTenant.id !== selectedTenantId) {
      setSelectedTenantId(activeTenant.id);
    }
  }, [activeTenant?.id]);

  const currentTenant = useMemo(() => {
    return tenants.find((t) => t.id === selectedTenantId) || activeTenant || tenants[0] || null;
  }, [tenants, selectedTenantId, activeTenant]);

  const [products, setProducts] = useState(initialProducts || []);
  const [categories, setCategories] = useState(initialCategories || []);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(initialError);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Override restore state
  const [confirmingRestoreId, setConfirmingRestoreId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [restoreFeedback, setRestoreFeedback] = useState({ id: null, message: '', isError: false });

  // Bulk Markup State (read-only / disabled in current release)
  const [bulkCategory, setBulkCategory] = useState('all');
  const [bulkMarkupPercent, setBulkMarkupPercent] = useState('15');

  // Load Categories & Products for current tenant with admin authorization
  useEffect(() => {
    let isMounted = true;

    async function loadCatalog() {
      if (!currentTenant) return;
      setLoading(true);
      setFetchError(null);
      try {
        const currentApiKey = apiKey || getAdminApiKey();
        const headers = {};
        if (currentApiKey) {
          headers['Authorization'] = `Bearer ${currentApiKey}`;
        }

        const [catsRes, prodsRes] = await Promise.all([
          apiFetch(`/api/categories?tenant_id=${encodeURIComponent(currentTenant.id)}`, {
            tenantId: currentTenant.id,
            headers,
            token: currentApiKey,
          }),
          apiFetch(`/api/products?tenant_id=${encodeURIComponent(currentTenant.id)}&limit=100&sort=recommended`, {
            tenantId: currentTenant.id,
            headers,
            token: currentApiKey,
          }),
        ]);

        if (isMounted) {
          if (catsRes?.categories && Array.isArray(catsRes.categories)) {
            setCategories(catsRes.categories);
          } else {
            setCategories([]);
          }
          if (prodsRes?.products && Array.isArray(prodsRes.products)) {
            setProducts(prodsRes.products);
          } else {
            setProducts([]);
          }
          setFetchError(null);
        }
      } catch (err) {
        console.error('Failed to load assortment:', err);
        if (isMounted) {
          setCategories([]);
          setProducts([]);
          setFetchError(err.message || 'Sortiment nicht verfügbar / Verbindung zum Server fehlgeschlagen.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadCatalog();
    return () => {
      isMounted = false;
    };
  }, [currentTenant, apiKey]);

  const executeRestore = async (product) => {
    if (!product || !currentTenant?.id) return;
    const prodId = product.id || product.sku;
    setRestoringId(prodId);
    setRestoreFeedback({ id: null, message: '', isError: false });

    try {
      const res = await updateAdminProductDisplay(prodId, currentTenant.id, {
        description: null,
      });

      setProducts((prev) =>
        prev.map((item) => {
          if ((item.id && item.id === product.id) || (item.sku && item.sku === product.sku)) {
            const restoredName = res?.name?.effective || res?.name?.original || item.originalName || item.name;
            const restoredDesc = res?.description?.effective || res?.description?.original || item.originalDescription || item.description;
            return {
              ...item,
              name: restoredName,
              description: restoredDesc,
              isOverridden: false,
              is_overridden: false,
              customDescription: null,
              custom_description: null,
            };
          }
          return item;
        })
      );

      setConfirmingRestoreId(null);
      setRestoreFeedback({ id: prodId, message: PRODUCT_EDIT_TEXTS.STATE_RESTORED, isError: false });
      setTimeout(() => {
        setRestoreFeedback((cur) => (cur.id === prodId ? { id: null, message: '', isError: false } : cur));
      }, 3000);
    } catch (err) {
      setRestoreFeedback({
        id: prodId,
        message: err?.message || 'Wiederherstellen fehlgeschlagen. Bitte erneut versuchen.',
        isError: true,
      });
    } finally {
      setRestoringId(null);
    }
  };

  // Filtered Products
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchSearch =
        !searchQuery.trim() ||
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchCategory =
        selectedCategory === 'all' ||
        p.category_id === selectedCategory ||
        p.categoryId === selectedCategory ||
        p.category_slug === selectedCategory;

      return matchSearch && matchCategory;
    });
  }, [products, searchQuery, selectedCategory]);

  return (
    <div className="space-y-6">
      {/* Header & Tenant Selector */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-[var(--border)] pb-4">
        <div>
          <h2 className="font-display text-xl font-bold text-[var(--foreground)]">
            Sortiment & Preis-Overlays
          </h2>
          <p className="text-xs text-[var(--secondary)]">
            Verwalten Sie Sichtbarkeiten, Highlight-Artikel und individuelle B2B-Verkaufspreise je Partner-Mandant.
          </p>
        </div>

        {tenants.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-700">Mandant:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => {
                setSelectedTenantId(e.target.value);
                const found = tenants.find((t) => t.id === e.target.value);
                if (found && onSelectTenant) onSelectTenant(found);
              }}
              className="rounded-md border border-[var(--border)] bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-2xs focus:ring-2 focus:ring-[var(--primary)]"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Bulk Markup Calculator Tool */}
      <Card className="border-[var(--border)] bg-slate-50/70 shadow-xs">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-slate-500" />
              <CardTitle className="text-sm font-bold">Massen-Kalkulation (Bulk Markup)</CardTitle>
            </div>
            <Badge variant="outline" className="text-amber-800 bg-amber-50 border-amber-300 text-[10px] font-medium">
              Funktion in aktuellem Release nicht angebunden
            </Badge>
          </div>
          <CardDescription className="text-xs">
            Wenden Sie prozentuale Margenaufschläge pauschal auf eine Warengruppe oder das gesamte Sortiment an.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 opacity-60">
            <div className="space-y-1">
              <span className="text-[11px] font-semibold text-slate-600 block">Kategorie:</span>
              <select
                disabled
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
                className="rounded-md border border-slate-300 bg-slate-100 px-3 py-1.5 text-xs font-medium cursor-not-allowed"
              >
                <option value="all">Alle Kategorien</option>
                {categories.map((c) => (
                  <option key={c.id || c.slug} value={c.id || c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <span className="text-[11px] font-semibold text-slate-600 block">Aufschlag (%):</span>
              <div className="flex items-center gap-1.5">
                <Input
                  disabled
                  type="number"
                  step="1"
                  value={bulkMarkupPercent}
                  onChange={(e) => setBulkMarkupPercent(e.target.value)}
                  className="w-24 text-xs font-mono font-bold bg-slate-100 cursor-not-allowed"
                  placeholder="15"
                />
                <span className="text-xs text-slate-500 font-bold">%</span>
              </div>
            </div>

            <div className="self-end">
              <Button size="sm" disabled title="Funktion in aktuellem Release nicht angebunden" className="gap-1.5 text-xs opacity-60 cursor-not-allowed">
                <TrendingUp className="h-3.5 w-3.5" />
                Funktion in aktuellem Release nicht angebunden
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {fetchError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Sortiment nicht verfügbar</AlertTitle>
          <AlertDescription>{fetchError}</AlertDescription>
        </Alert>
      )}

      {/* Filter Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-72">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Artikel suchen (Name, SKU, Hersteller)..."
              className="pl-9 text-xs"
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-white px-3 py-2 text-xs font-medium text-slate-700"
          >
            <option value="all">Alle Kategorien</option>
            {categories.map((c) => (
              <option key={c.id || c.slug} value={c.id || c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="text-xs text-slate-500 font-medium">
          {filteredProducts.length} Artikel angezeigt
        </div>
      </div>

      {/* Assortment Table */}
      <Card className="border-[var(--border)] bg-white shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="font-semibold text-xs">Artikel / SKU</TableHead>
                <TableHead className="font-semibold text-xs">Kategorie</TableHead>
                <TableHead className="font-semibold text-xs text-right">Einkaufspreis (EK)</TableHead>
                <TableHead className="font-semibold text-xs text-right">Verkaufspreis (VK)</TableHead>
                <TableHead className="font-semibold text-xs text-right">Marge</TableHead>
                <TableHead className="font-semibold text-xs text-center">Highlight</TableHead>
                <TableHead className="font-semibold text-xs text-center">Sichtbar</TableHead>
                <TableHead className="font-semibold text-xs text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin text-[var(--primary)]" />
                      <span>Sortiment wird geladen...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : fetchError ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-rose-600 font-medium">
                    Sortiment konnte nicht geladen werden. Bitte Verbindung prüfen.
                  </TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-slate-500">
                    Keine Artikel gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((p) => {
                  const base = p.base_price || p.basePrice || 0;
                  const effective = p.custom_price || p.customPrice || base;
                  const isHidden = Boolean(p.is_hidden);
                  const isFeatured = Boolean(p.is_featured);

                  const marginCents = effective - base;
                  const marginPercent = base > 0 ? ((marginCents / base) * 100).toFixed(1) : 0;
                  const hasCustomPrice = effective !== base;
                  const isOverridden = Boolean(
                    p.isOverridden ||
                    p.is_overridden ||
                    p.custom_description ||
                    p.displayNameOverride ||
                    p.customDescription
                  );
                  const prodId = p.id || p.sku;
                  const isConfirming = confirmingRestoreId === prodId;
                  const isRestoring = restoringId === prodId;
                  const feedback = restoreFeedback.id === prodId ? restoreFeedback : null;

                  return (
                    <TableRow
                      key={prodId}
                      className={`hover:bg-slate-50/60 transition-colors ${
                        isHidden ? 'opacity-50 bg-slate-50/40' : ''
                      }`}
                    >
                      {/* Product Name & SKU */}
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-semibold text-xs text-[var(--foreground)] line-clamp-1">
                            {p.name}
                          </span>
                          {isOverridden && (
                            <Badge
                              variant="outline"
                              data-testid="override-badge"
                              className="text-amber-800 bg-amber-50 border-amber-300 text-[10px] font-bold"
                            >
                              Überschrieben
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] font-mono text-slate-400">
                          {`${p.manufacturer ? `${p.manufacturer} • ` : ''}SKU: ${p.sku}`}
                        </div>
                        {feedback && (
                          <div
                            className={`mt-1 text-[11px] font-medium flex items-center gap-1 ${
                              feedback.isError ? 'text-red-600' : 'text-emerald-700'
                            }`}
                          >
                            {!feedback.isError && <Check className="h-3 w-3 text-emerald-600" />}
                            <span>{feedback.message}</span>
                          </div>
                        )}
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] font-normal">
                          {p.category_name || p.categoryName || p.category_id || 'Allgemein'}
                        </Badge>
                      </TableCell>

                      {/* Wholesale Base Price */}
                      <TableCell className="text-right font-mono text-xs text-slate-600">
                        {formatCurrency(base)}
                      </TableCell>

                      {/* Effective Selling Price */}
                      <TableCell className="text-right">
                        <div className="font-mono text-xs font-bold text-slate-900">
                          {formatCurrency(effective)}
                        </div>
                        {hasCustomPrice && (
                          <span className="text-xs font-semibold text-blue-600 block">
                            Individuell
                          </span>
                        )}
                      </TableCell>

                      {/* Margin % */}
                      <TableCell className="text-right font-mono text-xs">
                        <span
                          className={`font-semibold ${
                            marginCents > 0
                              ? 'text-emerald-700'
                              : marginCents < 0
                              ? 'text-rose-600'
                              : 'text-slate-500'
                          }`}
                        >
                          {marginCents > 0 ? `+${marginPercent}%` : `${marginPercent}%`}
                        </span>
                      </TableCell>

                      {/* Featured Toggle */}
                      <TableCell className="text-center">
                        <button
                          type="button"
                          disabled
                          className={`p-1 rounded-md transition-colors opacity-60 cursor-not-allowed ${
                            isFeatured
                              ? 'text-amber-500 bg-amber-50'
                              : 'text-slate-300'
                          }`}
                          title="Funktion in aktuellem Release nicht angebunden"
                        >
                          <Star className={`h-4 w-4 ${isFeatured ? 'fill-amber-500' : ''}`} />
                        </button>
                      </TableCell>

                      {/* Visibility Toggle */}
                      <TableCell className="text-center">
                        <button
                          type="button"
                          disabled
                          className={`p-1 rounded-md transition-colors opacity-60 cursor-not-allowed ${
                            isHidden
                              ? 'text-rose-600 bg-rose-50'
                              : 'text-emerald-600 bg-emerald-50'
                          }`}
                          title="Funktion in aktuellem Release nicht angebunden"
                        >
                          {isHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        {isOverridden ? (
                          isConfirming ? (
                            <div className="flex items-center justify-end gap-1" data-testid="restore-confirm-box">
                              <span className="text-[11px] text-amber-800 font-semibold mr-1">Zurücksetzen?</span>
                              <Button
                                size="sm"
                                disabled={isRestoring}
                                onClick={() => executeRestore(p)}
                                data-testid="confirm-restore-btn"
                                className="h-6 text-[11px] bg-red-600 hover:bg-red-700 text-white px-2 cursor-pointer font-bold"
                              >
                                {isRestoring ? 'Setzt zurück…' : 'Ja'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isRestoring}
                                onClick={() => setConfirmingRestoreId(null)}
                                data-testid="cancel-restore-btn"
                                className="h-6 text-[11px] px-2 cursor-pointer"
                              >
                                Nein
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmingRestoreId(prodId)}
                              data-testid="restore-original-button"
                              title="Original wiederherstellen"
                              className="h-7 text-xs px-2 gap-1 text-slate-700 hover:text-red-600 hover:border-red-300 transition-colors cursor-pointer"
                            >
                              <RotateCcw className="h-3 w-3" />
                              <span>Original wiederherstellen</span>
                            </Button>
                          )
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled
                            title="Funktion in aktuellem Release nicht angebunden"
                            className="h-7 text-xs px-2 gap-1 opacity-60 cursor-not-allowed"
                          >
                            <DollarSign className="h-3 w-3" />
                            <span>Gesperrt</span>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
